import { useEffect, useRef, useState, memo, useCallback } from 'react'
import styled from 'styled-components'
import { useAppSelector, useIsElectron } from 'app/hooks'
import { useNavigate } from 'react-router-dom'
import { t } from 'i18next'
import { Tooltip } from "@progress/kendo-react-tooltip";

import colors from 'utils/colors'
import { FixMeLater } from 'types'
import {
  CONFERENCE_BLANK_DIMENSIONS,
  CONFERENCE_TYPE_VIDEO,
  MESSAGE_DATE_FORMAT,
  MOBILE_WIDTH,
} from 'utils/constants'
import { BUTTONS, MESSAGES, MODALS } from 'locales/locales'
import { IRoom, RoomData, RoomTypes } from 'types/rooms.types'
import { useCreateConversationMutation, useCreateMeetingConversationMutation } from 'app/services/room.service'
import { IMessage, MessageStatus, MessageUserType } from 'types/conversation.types'
import { useLazyCreateChannelConferenceQuery } from 'app/services/conference.service'

import Header from './header/Header'
import Option from './option/Option'
import Status from './status/Status'
import Content from './content/Content'
import Reaction from './reaction/Reaction'
import UserCard from './user-card/UserCard'
import Loading from 'components/spinner/Loading'
import CustomText from 'components/text/CustomText'
import SystemMessage from './system-message/SystemMessage'
import Avatar, { AvatarSize } from 'components/avatar/Avatar'
import ThreadDraftInfo from './thread-draft-info/ThreadDraftInfo'
import useInfoModal from 'utils/hooks/useInfoModal'
import moment from 'moment'

import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { IRolePermissions, TeamAndChannelPermissions } from 'types/role.types'
import UserGroupsModal from 'features/channels/userGroupModal'
import MessageReactions from 'features/messages/message-reactions/MessageReactions'
import OtrOfflineUserList from 'features/messages/OtrOfflineUserList'
import CustomIcon from 'components/icon/CustomIcon'
import { icons } from 'utils/icons'
import { Checkbox } from '@progress/kendo-react-inputs'
import { useCreateConference } from 'utils/hooks/useCreateConference'

interface INewMessageProps {
  isOtr?: boolean
  roomId?: string
  myId: string
  index: number
  data: IMessage
  draftData?: FixMeLater
  isSearch?: boolean
  jumpToIndex?: number
  visibleOption?: boolean
  visibleThread?: boolean
  visibleReaction?: boolean
  threadMainMessage?: boolean
  searchTextInMessage?: string
  selectionMode?: { isSelectionMode: boolean; selectedMessages: string[] }
  isSelected?: boolean
  setSelectionMode?: (selectionMode: { isSelectionMode: boolean; selectedMessages: string[] }) => void
  onTogglePinMessage?: (messageId: string, pinned: boolean) => void
  onDeleteMessage?: (messageId: string) => void
  onReactionMessage?: (messageId: string, emoji: string, isAdd: boolean) => void
  onReplyMessage?: (visible: boolean, type: string, params?: { messageId: string }) => void
  onToggleMessageSelection?: (messageId: string) => void
  onJumpMessage?: (messageId: string, threadId?: string) => void
  onResetJumpToIndex?: (item: number) => void
  onResetThreadId?: () => void
  onReadMessage?: (visible: boolean, type: string, params?: { messageId: string }) => void
}
const NewMessage = ({
  isOtr,
  roomId,
  myId,
  data,
  index,
  draftData,
  jumpToIndex,
  isSearch,
  visibleOption = true,
  visibleThread,
  visibleReaction = true,
  searchTextInMessage,
  threadMainMessage,
  selectionMode = { isSelectionMode: false, selectedMessages: [] },
  isSelected = false,
  onTogglePinMessage,
  onDeleteMessage,
  onReactionMessage,
  onResetJumpToIndex,
  onReplyMessage,
  onJumpMessage,
  onResetThreadId,
  setSelectionMode,
  onToggleMessageSelection,
  onReadMessage
}: INewMessageProps) => {

  const messageRef = useRef<HTMLDivElement>(null)
  const avatarRef = useRef<HTMLDivElement>(null)
  const roomRef = useRef<IRoom | undefined>(undefined)
  const [reactionsState, setReactionsState] = useState<{
    visible: boolean
    reactions: { emoji: string; users: string[] }[]
  }>({ visible: false, reactions: [] })

  const [isEditMessage, setEditMessage] = useState(false)
  const [windowApi,setWindowApi] = useState<FixMeLater>(null);

  const [visibleEmojiPicker, setVisibleEmojiPicker] = useState(false)
  const [visibleUserCard, setVisibleUserCard] = useState<{
    visible: boolean
    userId: string | null
    fullname:string | null
  }>({ visible: false, userId: null , fullname:null})
  const [offlineUsersModal, setOfflineUsersModal] = useState({
    offlineUsers: (data as any)?.offlineUsers || [],
    isVisible: false,
  })

  const {
    openModal: openConferenceModal,
    closeModal: closeConferenceModal,
    Modal: ConferenceModal,
  } = useInfoModal()

  const navigate = useNavigate()
  const rooms: RoomData = useAppSelector((state) => state.rooms.data)
  const [createConversation] = useCreateConversationMutation()
  const [createConference] = useLazyCreateChannelConferenceQuery()
  const [isUserGroupsModalVisible, setIsUserGroupsModalVisible] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState("");

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const messageAlignmentClass = data.sender === myId ? 'right' : 'left';
  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)
  const hasViewUserCard = isFlagSetIn(
    [TeamAndChannelPermissions.VIEW_TEAM_MEMBERS],
    permissions.teamAndChannel,
  )

  useEffect(() => {
    const w = window as any
    if (w.api) setWindowApi(w.api)
  }, [])

  useEffect(() => {
    if (rooms.length > 0) {
      roomRef.current = rooms.find((room) => room._id === roomId)
    }
  }, [rooms])


  const onUsersGrup = useCallback((userId: string, fullname: string) => {
    setIsUserGroupsModalVisible(true);
    setSelectedUserId(userId);
    setSelectedUserName(fullname);
  }, []);

  const closeUserGroupsModal = useCallback(() => {
    setIsUserGroupsModalVisible(false);
  }, []);


  useEffect(() => {
    if (jumpToIndex === index) {
      if (messageRef?.current) {
        messageRef.current.style.transition = 'backgroundColor 1s cubic-bezier(.2,.94,.93,.95)'
        messageRef.current.style.backgroundColor = `${colors.vime10}`
      }
      messageRef?.current?.scrollIntoView({
        behavior: 'auto',
      })
      setTimeout(() => {
        if (messageRef?.current) {
          messageRef.current.style.backgroundColor = ''
          onResetJumpToIndex?.(-1)
          onResetThreadId?.()
        }
      }, 2000)
    }
  }, [jumpToIndex])

  const onClickChatIcon = useCallback(async (userId: string) => {
    try {
      Loading.ref.show()
      const result: FixMeLater = await createConversation({ members: [userId] })
      if (!result.error) {
        const roomId = typeof result.data.data === 'string' ? result.data.data : result.data.data._id;
        navigate('/messages/' + roomId);
        return roomId;
    }
    } finally {
      Loading.ref.hide()
    }
  }, [visibleUserCard.userId, navigate, createConversation])

  const onKeyUp = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && visibleUserCard.visible) {
      setVisibleUserCard({ visible: false, userId: null, fullname:null })
    }
  }, [visibleUserCard.visible, setVisibleUserCard])

  const onVisibleUserCard = useCallback(
    (userId: string, fullname: string) => {
      //TODO: DM'lerde usercard'ın enegellenmesi gerekiyor mu ?
      if (roomRef.current?.type !== RoomTypes.DM) {
        setVisibleUserCard({ visible: true, userId, fullname });
      }
    },
    [roomRef.current],
  );
  const isElectron = useIsElectron()
  const [createMeetingConversation] = useCreateMeetingConversationMutation()

  const { handleCreateConference } = useCreateConference({
    createConference,
    createMeetingConversation,
    isElectron,
    windowApi,
    closeConferenceModal,
  });

  const onVideoCall = useCallback(async (userId: string) => {
    openConferenceModal({
      title: t(MODALS.CREATE_VIDEO_CONFERENCE_TITLE),
      description: t(MODALS.CREATE_VIDEO_CONFERENCE_DESCRIPTION),
      cancelTitle: t(BUTTONS.CANCEL) as string,
      okTitle: t(BUTTONS.YES) as string,
      onCancel: closeConferenceModal,
      onOk: () => handleCreateConference(CONFERENCE_TYPE_VIDEO, [myId, userId], null),
    })
  }, [t, openConferenceModal, closeConferenceModal, handleCreateConference]);


  const onToggleVisiblePicker = useCallback(() => {
    setVisibleEmojiPicker((prevState) => !prevState)
  },[])

  const onClickBackDrop = useCallback(() => {
    setVisibleEmojiPicker(false);
    setEditMessage(false);
    setVisibleUserCard({
      visible: false,
      userId: null,
      fullname: null
    });
  }, []);

  const onCloseReactionsModal = useCallback(() => {
    setReactionsState({ visible: false, reactions: [] })
  }, []);

  const onShowReactionsModal = useCallback((reactions: { emoji: string; users: string[] }[]) => {
    setReactionsState({ reactions, visible: true })
  }, []);

  const onToggleEditMessage = useCallback(() => {
    setEditMessage((prevState) => !prevState)
  }, []);

  const otrMessageInfo = useCallback(() => {
    setOfflineUsersModal({offlineUsers:(data as any)?.offlineUsers ,isVisible:true})
  }, [data]);

  const onCloseOtrOfflineUsersModal = useCallback(() => {
    setOfflineUsersModal({offlineUsers:[], isVisible:false})
  }, []);

  const handleMessageSelection = useCallback(() => {
    if (selectionMode.isSelectionMode && onToggleMessageSelection && !data?.separator && data.sender !== MessageUserType.SYSTEM) {
      onToggleMessageSelection(data._id);
    }
  }, [selectionMode.isSelectionMode, onToggleMessageSelection, data]);

  if (data.sender === MessageUserType.SYSTEM) {
    return (
      <SystemMessage
        data={data?.content}
        users={data?.userData}
        searchTextInMessage={searchTextInMessage}
        onShowUserInfo={onVisibleUserCard}
      />
    )
  }

const readReceipt = (() => {
  if (data?.sender !== myId) return null;

  const currentRoom = rooms.find((room: any) => room._id === data.roomId);

  if (!currentRoom || !Array.isArray(currentRoom.members)) return null;
  if (!Array.isArray(data.readers)) return null;

  const senderId = data.sender;

  const membersExceptSender = currentRoom.members.filter(
    (id: any) => id?.toString?.() !== senderId?.toString?.()
  );
  const membersCount = membersExceptSender.length;

  const readersExceptSender = data.readers.filter(
    (id: any) => id?.toString?.() !== senderId?.toString?.()
  );
  const readersCount = readersExceptSender.length;

  const allRead = membersCount > 0 && readersCount === membersCount;

  const icon = allRead ? icons.doubleCheck : icons.doubleCheckGrey;

  return (
    <ReadReceiptWrapper>
      <Tooltip anchorElement="target" position="top" openDelay={250} >
        <CustomIcon source={icon} iconPreset="small" />
      </Tooltip>
    </ReadReceiptWrapper>
  );
})();




  return (
    <MessageContainer>
      <Wrapper
        className={messageAlignmentClass}
        ref={messageRef}
        isPinned={threadMainMessage ? false : data.pinned}
        visibleEmojiPicker={visibleEmojiPicker}
        isEdit={isEditMessage}
        subsequent={data?.subsequent ?? false}
        isSelectionMode={selectionMode.isSelectionMode}
        isSelected={isSelected}
        onClick={handleMessageSelection}
      >
        {selectionMode.isSelectionMode && !data?.separator && (
          <Checkbox
            checked={isSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => handleMessageSelection()}
            style={{ border: `1px solid ${colors.gray80}` }}
          />
        )}
        {!data?.subsequent && (
          <Avatar
            type={'circle'}
            ref={avatarRef}
            source={data?.userData[data?.sender]?.avatar}
            text={data?.userData[data?.sender]?.fullname}
            size={AvatarSize.average}
            onClick={() =>
              !selectionMode.isSelectionMode &&
              onVisibleUserCard(data.sender, data?.userData[data?.sender]?.fullname)
            }
          />
        )}
        {data?.subsequent && (
          <Time>
            <StyledText id="date" typography="body8" color={colors.white}>
              {moment(data?.createdAt || data?.sentAt).format(MESSAGE_DATE_FORMAT)}
            </StyledText>
          </Time>
        )}
        <OutWrapper
          className={messageAlignmentClass}
          onClick={(e) => {
            if (selectionMode.isSelectionMode) {
              e.stopPropagation()
              handleMessageSelection()
            } else if (!data.isDeleted && data?.reactions?.length && !isEditMessage) {
              onShowReactionsModal(data.reactions)
            }
          }}
        >
          {!data?.subsequent && (
            <Header
              createdAt={data?.createdAt || data?.sentAt}
              user={data?.userData[data?.sender]}
            />
          )}
          <Content
            /* key={v4()} */
            roomId={roomId}
            users={data?.userData}
            messageId={data._id}
            data={data?.content}
            isDeleted={data.isDeleted}
            isEditMessage={isEditMessage}
            isMyMessage={data.sender === myId}
            isForwarded={data?.isForwarded ?? false}
            searchTextInMessage={searchTextInMessage}
            isPending={data.status === 'pending'}
            onShowUserInfo={(userId: string, fullname: string) =>
              !selectionMode.isSelectionMode &&
              onVisibleUserCard(userId, fullname)
            }
            onCancelEditMessage={onToggleEditMessage}
          />
          {data.isEdited && !data.isDeleted && (
            <EditedText typography="body8" color={colors.gray60}>
              {t(MESSAGES.EDITED_MESSAGE)}
            </EditedText>
          )}
          {readReceipt}
          {visibleReaction && !data.isDeleted && (
            <Reaction
              messageId={data._id}
              users={data?.userData}
              data={data?.reactions}
              onReactionMessage={onReactionMessage}
            />
          )}
          {visibleThread && (
            <ThreadDraftInfo
              messageId={data._id}
              users={data?.userData}
              data={data.repliedUsers}
              threadCount={data?.threadCount}
              draftData={draftData}
              onReplyMessage={onReplyMessage}
            />
          )}
        </OutWrapper>
        {visibleOption &&
          !data.isDeleted &&
          !isOtr &&
          !selectionMode.isSelectionMode &&
          data.status !== MessageStatus.FAILED &&
          data.status !== MessageStatus.PENDING && (
            <Option
              isMyMessage={myId === data.sender}
              messageId={data._id}
              isPinned={data.pinned}
              isSearch={isSearch}
              jumpId={data.threadId ? data.threadId : data._id}
              threadId={data.threadId ? data._id : ''}
              visibleEmojiPicker={visibleEmojiPicker}
              subsequent={data?.subsequent}
              isForwarded={data?.isForwarded ?? false}
              onTogglePinMessage={onTogglePinMessage}
              onDeleteMessage={onDeleteMessage}
              onReactionMessage={onReactionMessage}
              onReplyMessage={onReplyMessage}
              onReadMessage={onReadMessage}
              setSelectionMode={setSelectionMode}
              onJumpMessage={onJumpMessage}
              onToggleVisiblePicker={onToggleVisiblePicker}
              onEditMessage={onToggleEditMessage}
            />
          )}
        {!isOtr &&
          visibleUserCard.visible &&
          !data?.userData[visibleUserCard?.userId ?? data.sender]?.isDeleted &&
          hasViewUserCard && (
            <UserCard
              showChatIcon={myId !== data.sender}
              showCallIcon={myId !== data.sender}
              showCameraIcon={myId !== data.sender}
              positionY={avatarRef?.current?.getBoundingClientRect().y}
              onKeyUp={onKeyUp}
              isCentered={false}
              onClickUserGroups={() => {
                visibleUserCard.userId &&
                  visibleUserCard.fullname &&
                  onUsersGrup(visibleUserCard.userId, visibleUserCard.fullname)
              }}
              onClickCameraIcon={()=>onVideoCall(data.sender)}
              onClickChatIcon={()=>onClickChatIcon(data.sender)}
              {...data?.userData[visibleUserCard?.userId || data.sender]}
            />
          )}
        {data?.status && <Status data={data} />}
        {isOtr && (data as any)?.offlineUsers?.length > 0 && (
          <>
            <OTROfflineInfoWrapper
              title={t(MODALS.OTR_OFFLINE_USERS_CANT_GET_MESSAGE)}
              onClick={otrMessageInfo}
            >
              <Tooltip anchorElement="target" position="left" openDelay={250} parentTitle={true}>
                <CustomIcon source={icons.infoFailIcon} iconPreset="medium" />
              </Tooltip>
            </OTROfflineInfoWrapper>
          </>
        )}
      </Wrapper>
      {(visibleUserCard.visible || visibleEmojiPicker || isEditMessage) && (
        <BackDrop onClick={onClickBackDrop} />
      )}
      {offlineUsersModal.isVisible && (
        <OtrOfflineUserList
          onClose={onCloseOtrOfflineUsersModal}
          users={offlineUsersModal.offlineUsers}
        />
      )}
      {ConferenceModal}
      {isUserGroupsModalVisible && selectedUserId && (
        <UserGroupsModal
          onClose={closeUserGroupsModal}
          isVisible={isUserGroupsModalVisible}
          userId={selectedUserId}
          userName={selectedUserName}
        />
      )}
      {reactionsState.visible && (
        <MessageReactions
          onClose={onCloseReactionsModal}
          reactions={reactionsState.reactions}
          userData={data.userData}
        />
      )}
    </MessageContainer>
  )
}

export default memo(NewMessage)

const ReadReceiptWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;


const OTROfflineInfoWrapper = styled.div`  
  display: flex;
  justify-content: flex-end;
  filter: grayscale(100%);
  cursor: pointer;
`

const MessageContainer = styled.div``

const Wrapper = styled.div.attrs(
  (props: {
    isPinned: boolean
    visibleEmojiPicker: boolean
    isEdit: boolean
    myId: string
    subsequent: boolean
    senderId: string
    isSelectionMode: boolean
    isSelected: boolean
  }) => props,
)`
  display: flex;
  align-items: center;
  position: relative;
  padding: ${({ subsequent }) => (subsequent ? '2.5px 10px 10px 30px' : '7px 10px 7px 30px')};
  column-gap: 10px;
  background-color: ${({ isPinned, isSelected }) => {
    if (isSelected) return `${colors.light80}`;
    return isPinned ? colors.vime50 : 'initial';
  }};
  &.left {
    justify-content: flex-start;
  }

  &.right {
    -webkit-box-pack: end;
    justify-content: flex-start;
    flex-direction: row-reverse;
  }
  &:hover {
    background-color: ${colors.light70};
    cursor: pointer;
  }

  &:hover #option {
    display: ${({ isEdit, isSelectionMode }) => (!isEdit && !isSelectionMode ? 'flex' : 'none')};
  }

  &:hover #date {
    color: ${colors.gray60};
  }

  #option {
    display: ${({ visibleEmojiPicker }) => (visibleEmojiPicker ? 'flex' : 'none')};
  }

  @media (max-width: ${MOBILE_WIDTH - 400}px) {
    padding: ${({ subsequent }) => (subsequent ? '2.5px 18px' : '20px 10px 5px 8px')};
  }
`

const BackDrop = styled.div`
  position: fixed;
  z-index: 1;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  backdrop-filter: blur(1px);
`
const OutWrapper = styled.div`
  &.right {
    max-width: 450px;
    border-radius: 15px 15px 0 15px;
    background-color: #eaf9ff;
  }

  &.left {
    max-width: 450px;
    border-radius: 15px 15px 15px 0;
    background-color: #f6f6f6;
  }
  background-color: #f0f0f0;
  border-radius: 20px;
  padding: 10px;
  margin: 5px 0;
  max-width: 70%;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  word-wrap: break-word;
  cursor: pointer;
`
const EditedText = styled(CustomText).attrs((props: { isPendingTextStyle: boolean }) => props)`
  color: ${({ isPendingTextStyle }) => (isPendingTextStyle ? ` ${colors.gray60}` : 'initial')};
  word-break: break-word;
  white-space: pre-line;
  font-style: italic;
`

const Time = styled.div`
  flex-shrink: 0;
`

const StyledText = styled(CustomText)`
  min-width: 30px;
`
